import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { gameCharsQueryKey } from '../../../../../../api/queries'
import { queryGameCharacters } from '../../../../../../api/queries'
import { IGameCharacter, IStatUpdate } from '../../../../../../api/types/character'
import { ErrorHandler } from '../../../../../../components/ErrorHandler/ErrorHandler'
import { fromAxiosError, useGlobalError } from '../../../../../../context/GlobalError'
import { IGame } from '../../../../../../api/types/game'
import { UserCharBadgeList } from './UserCharBadgeList/UserCharBadgeList'
import { CharacterSheet } from '../CharacterSheet/CharacterSheet'
import { NpcCharBadgeList } from './NpcCharBadgeList/NpcCharBadgeList'
import {
  mutationUpdateCharacterSkill,
  mutationUpdateCharacterStat,
  mutationUpdateCharacterWeaponProf,
} from '../../../../../../api/mutations'
import { NpcSayPanel } from './NpcSayPanel/NpcSayPanel'
import { extractMessage } from '../../../../../../utils'

interface Props {
  game: IGame
  showChatTab?: () => void
}

export const GmCharactersPanel: React.FC<Props> = ({ game, showChatTab }) => {
  const {
    isLoading,
    data: chars,
    error,
  } = useQuery<IGameCharacter[]>(gameCharsQueryKey(game.id), () => queryGameCharacters(game.slug), {
    onSuccess: data => {
      setSelectedChar(data?.find(c => c.id === selectedChar?.id))
    },
  })
  const [selectedChar, setSelectedChar] = useState<IGameCharacter>()
  const queryClient = useQueryClient()
  const { addError } = useGlobalError()
  const { mutate: updateStat } = useMutation(
    (update: IStatUpdate) => mutationUpdateCharacterStat(update),
    {
      onError: (err: any) => {
        addError(extractMessage(err) || 'Nie udało się zmienić atrybutu', err?.status)
      },
      onSuccess: (data: any) => {
        queryClient.invalidateQueries(gameCharsQueryKey(game.id))
      },
    }
  )
  const { mutate: updateSkill } = useMutation(
    (update: IStatUpdate) => mutationUpdateCharacterSkill(update),
    {
      onError: (err: any) => {
        addError(extractMessage(err) || 'Nie udało się zmienić umiejętności', err?.status)
      },
      onSuccess: (data: any) => {
        queryClient.invalidateQueries(gameCharsQueryKey(game.id))
      },
    }
  )
  const { mutate: updateWeaponProf } = useMutation(
    (update: IStatUpdate) => mutationUpdateCharacterWeaponProf(update),
    {
      onError: (err: any) => {
        addError(extractMessage(err) || 'Nie udało się zmienić biegłości', err?.status)
      },
      onSuccess: (data: any) => {
        queryClient.invalidateQueries(gameCharsQueryKey(game.id))
      },
    }
  )

  return (
    <>
      <ErrorHandler loading={isLoading} error={fromAxiosError(error)}>
        {chars && (
          <>
            <UserCharBadgeList
              onCharSelect={setSelectedChar}
              selected={selectedChar}
              game_chars={chars.filter(c => !!c.user)}
            />
            <NpcCharBadgeList
              onCharSelect={setSelectedChar}
              selected={selectedChar}
              clearSelection={() => setSelectedChar(undefined)}
              game_chars={chars.filter(c => !c.user)}
              game_id={game.id}
            />
            {selectedChar && (
              <>
                <CharacterSheet
                  showChatTab={showChatTab}
                  onEditAttribute={(name: string, newValue: string) =>
                    updateStat({
                      character_id: selectedChar.character.id,
                      name: name,
                      value: newValue,
                    })
                  }
                  onEditSkill={(name: string, newValue: string) =>
                    updateSkill({
                      character_id: selectedChar.character.id,
                      name: name,
                      value: newValue,
                    })
                  }
                  onEditWeaponProf={(name: string, newValue: string) =>
                    updateWeaponProf({
                      character_id: selectedChar.character.id,
                      name: name,
                      value: newValue,
                    })
                  }
                  editable={true}
                  char={selectedChar.character}
                  game_id={selectedChar.game_id}
                />
                {!selectedChar.user && (
                  <NpcSayPanel
                    showChatTab={showChatTab}
                    game={game}
                    character={selectedChar.character}
                  />
                )}
              </>
            )}
          </>
        )}
      </ErrorHandler>
    </>
  )
}
